import { createFileRoute, Navigate, Outlet, redirect } from '@tanstack/react-router';

import NotificationCenter from '@src/components/NotificationCenter';
import { ActiveOrgProvider, LocationProvider, PermissionsProvider, SidebarProvider } from '@src/context';
import { useActiveOrg } from '@src/context/hooks';
import { GlobalToaster, Sidebar, SubscriptionBanner, VerifyEmailBanner } from '@src/layout';
import { useMeQuery, useMineQuery } from '@src/query';

export const Route = createFileRoute('/app')({
  beforeLoad: async ({ context, location }) => {
    const { auth } = context;
    try {
      await auth.verifyToken();
    }
    catch {
      try {
        await auth.refreshToken();
      }
      catch {
        throw redirect({
          to: '/auth/login',
          search: {
            redirect: encodeURIComponent(location.href),
          },
        });
      }
    }
  },
  component: App,
});

/** Inner shell — runs inside ActiveOrg/Permissions providers so the banner,
 * bell and per-seller LocationProvider all follow the ACTIVE org rather than a
 * hard-coded memberships[0] (#213). */
function AppShell() {
  const { membership, seller } = useActiveOrg();
  const sellerId = seller?.id;

  return (
    <LocationProvider sellerId={sellerId}>
      <div className="flex min-h-screen bg-canvas text-ink">
        <nav><Sidebar /></nav>

        <main className="flex h-screen flex-1 flex-col gap-4 overflow-auto p-6 max-lg:p-3">
          <VerifyEmailBanner />
          <SubscriptionBanner membership={membership} />
          {/* Keyed by org so EVERY page remounts on an org switch — page-local
              filters, search, modals, and selections must never leak between
              tenants (the per-page seller keys cover only Inventory/Listings). */}
          <Outlet key={sellerId || 'no-org'} />
        </main>
      </div>

      {/* Shared notification bell (#165) — pinned top-right above every page's
          own top bar, since the app has no single shared header. */}
      {sellerId
        ? (
          <div className="fixed right-5 top-[18px] z-40">
            <NotificationCenter sellerId={sellerId} />
          </div>
        )
        : null}

      <GlobalToaster />
    </LocationProvider>
  );
}

AppShell.displayName = 'AppShell';


function App() {
  const { data: memberships, isSuccess } = useMineQuery();
  const { data: me } = useMeQuery();

  // First-run: an authenticated user with no active org goes to onboarding (UX 1.5).
  if (isSuccess && memberships?.length === 0) {
    return <Navigate replace to="/onboarding" />;
  }

  // ActiveOrg owns the selected membership (persisted per user); Permissions
  // derives from it; both wrap the shell and every page beneath the Outlet.
  return (
    <SidebarProvider>
      <ActiveOrgProvider userId={me?.id} memberships={memberships}>
        <PermissionsProvider>
          <AppShell />
        </PermissionsProvider>
      </ActiveOrgProvider>
    </SidebarProvider>
  );
}
